import React from 'react';
import { Button, Row, Col, Input, Radio } from 'antd';
import { FileOutlined } from '@ant-design/icons';

import { checkIsMoment, formatDateDisplay } from 'utils/date';
import { constructDisplayPrice } from 'utils/general';

import { FilterRadioContainer, FilterRadioButton, FilterSearchContainer, SearchIcon } from './table.styles';

const { Group } = Radio;

const handleOnConfirm = confirm => () => {
  confirm();
};

const handleOnReset = clearFilters => () => {
  clearFilters();
};

const FilterRadio = ({ filterSelections, setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
  <FilterRadioContainer>
    <Group
      onChange={e => {
        const value = e.target.value;
        setSelectedKeys(value ? [value] : []);
      }}
      value={selectedKeys[0]}
    >
      {filterSelections.map(selection => (
        <Row key={selection.label}>
          <FilterRadioButton value={selection.value}>{selection.label}</FilterRadioButton>
        </Row>
      ))}
    </Group>
    <Row gutter={8} type="flex" justify="space-between">
      <Col>
        <Button type="primary" onClick={handleOnConfirm(confirm)}>
          Search
        </Button>
      </Col>
      <Col>
        <Button onClick={handleOnReset(clearFilters)}>Reset</Button>
      </Col>
    </Row>
  </FilterRadioContainer>
);

const FilterSearch = ({ dataIndex, placeholder, setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
  return (
    <FilterSearchContainer>
      <Input
        id={dataIndex}
        placeholder={placeholder}
        value={selectedKeys[0]}
        onChange={e => {
          const value = e.target.value;
          setSelectedKeys(value ? [value] : []);
        }}
        onPressEnter={handleOnConfirm(confirm)}
      />
      <Button style={{ margin: '0 4px' }} type="primary" onClick={handleOnConfirm(confirm)}>
        Search
      </Button>
      <Button onClick={handleOnReset(clearFilters)}>Reset</Button>
    </FilterSearchContainer>
  );
};

export const constructColumn = (title, dataIndex, { width, hasSorter, isDate, isDocument, isRM } = {}) => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  ...(hasSorter && constructColumnSorter(dataIndex)),
  render: data => {
    if (!!data) {
      let text = <span>{data}</span>;

      if (checkIsMoment(data) || isDate) {
        text = <span>{formatDateDisplay(data)}</span>;
      } else if (isDocument) {
        text = (
          <a href={data.url} target="_blank" rel="noopener noreferrer">
            <FileOutlined />
          </a>
        );
      } else if (isRM) {
        text = <span>{constructDisplayPrice(data)}</span>;
      }

      return text;
    } else {
      return <span>-</span>;
    }
  }
});

export const getColumnFilterRadioProps = (dataIndex, filterSelections) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <FilterRadio filterSelections={filterSelections} setSelectedKeys={setSelectedKeys} selectedKeys={selectedKeys} confirm={confirm} clearFilters={clearFilters} />
  ),
  filterIcon: filtered => <SearchIcon type="filter" style={{ color: filtered ? '#00b9c6' : '#aaa' }} />,
  onFilter: (value, record) => {
    return String(record[dataIndex]).toLowerCase().includes(value.toLowerCase());
  }
});

export const getColumnFilterSearchProps = (dataIndex, placeholder) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <FilterSearch dataIndex={dataIndex} placeholder={placeholder} setSelectedKeys={setSelectedKeys} selectedKeys={selectedKeys} confirm={confirm} clearFilters={clearFilters} />
  ),
  filterIcon: filtered => <SearchIcon type="search" isFiltered={filtered} />,
  onFilter: (value, record) => {
    return String(record[dataIndex]).toLowerCase().includes(value.toLowerCase());
  },
  onFilterDropdownVisibleChange: visible => visible && setTimeout(() => document.getElementById(dataIndex).focus())
});

export const handleOnAggregationTableChange = async ({ pagination, filters, sorter }, setNewAggregationQuery) => {
  const filter = Object.entries(filters).reduce((filterQuery, [key, values]) => ({ ...filterQuery, ...(!!values && { [key]: values[0] }) }), {});
  const { current, pageSize } = pagination;
  const { columnKey, order } = sorter;
  const query = {
    ...(Object.keys(filter).length > 0 && { filter }),
    ...(sorter && columnKey && { sorter: { [columnKey]: order === 'descend' ? -1 : 1 } }),
    ...(pagination && current && { limit: pageSize, currentPage: current })
  };
  setNewAggregationQuery(query);
};

const constructColumnSorter = dataIndex => ({
  sorter: (nextRecord, currentRecord) => {
    const nextValue = String(nextRecord[dataIndex]).toUpperCase();
    const currentValue = String(currentRecord[dataIndex]).toUpperCase();

    return nextValue.localeCompare(currentValue);
  }
});
